/**
 * LinkMenu: a pill that opens a small list of outbound links (the places under the reviewer chip). The raw
 * react-aria menu stays in strata; the caller hands in the label and the entries, each an address and its words.
 * The pill is named by what it opens and then the words it shows ("Reviewer places: <email>"), so its name
 * contains its visible text.
 */
import type { ReactNode } from 'react';
import { Button, Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import { RADIUS, SPACE, TEXT, Z, usePalette } from '../theme';
import { focusRing } from './action-button';
import { OUTLINE_QUIET, chipStyle } from './chip';

export type MenuEntry = { key: string; label: ReactNode; href: string; newTab: boolean };

export function LinkMenu({ label, entries, ariaLabel }: { label: string; entries: readonly MenuEntry[]; ariaLabel: string }) {
  const p = usePalette();
  return (
    <MenuTrigger>
      <Button
        aria-label={`${ariaLabel}: ${label}`}
        style={(s) => ({ ...chipStyle(p, OUTLINE_QUIET, 'bar', false), cursor: 'pointer', outline: 'none', boxShadow: s.isFocusVisible ? focusRing(p) : undefined })}
      >
        {label}
        <span aria-hidden="true" style={{ color: p.inkMuted }}>
          ▾
        </span>
      </Button>
      <Popover
        placement="bottom end"
        style={{ zIndex: Z.popover, background: p.surface, border: `1px solid ${p.hairline}`, borderRadius: RADIUS.md, boxShadow: `0 8px 24px ${p.shadow}` }}
      >
        <Menu aria-label={ariaLabel} style={{ padding: SPACE.xs, outline: 'none', minWidth: 160 }}>
          {entries.map((entry) => (
            <MenuItem
              key={entry.key}
              id={entry.key}
              href={entry.href}
              {...(entry.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              style={(s) => ({
                padding: `${SPACE.tight}px ${SPACE.md}px`,
                borderRadius: RADIUS.sm,
                fontSize: TEXT.control,
                color: p.ink,
                background: s.isFocused || s.isHovered ? p.surfaceRaised : 'transparent',
                cursor: 'pointer',
                outline: 'none',
              })}
            >
              {entry.label}
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
